import { Component, Input, OnInit } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { FormBuilder, Validators, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { default as swal } from 'sweetalert2';
import { first } from 'rxjs/operators';
import { MessageService } from '@app/shared/services';
import { TypeResponse } from '@app/shared/enum/TypeResponse';
import { StudentFeedback } from '@app/student/model';
import {
  InitStudentService,
  StudentFeedbackService,
} from '@app/student/services';
import { User } from '@app/auth/model';
import { Student } from '@app/admin/model';
import { CustomValidators } from '@app/shared/validators/custom-validators';

@Component({
  selector: 'app-feedback-exp-class',
  templateUrl: './feedbackExpClass.component.html',
})
export class FeedbackExpClassComponent implements OnInit {
  @Input('studentClassesCourseId') studentClassesCourseId: string;
  @Input('isStudent') isStudent: boolean = false;

  public feedbackForm: FormGroup; // Novo FormGroup
  public validEntity: boolean = true;
  public submitted = false;
  public errorMessage: string = '';
  public user: User = new User();
  private student: Student;

  public maxLengthFeedback: number = 300; // limite de caracteres para o feedback

  public studendFeedback: StudentFeedback;
  public preview: boolean = false;

  constructor(
    private router: Router,
    private modalService: NgbModal,
    private formBuilder: FormBuilder,
    private loading: MessageService,
    private initStudent: InitStudentService,
    private studentFeedbackService: StudentFeedbackService
  ) {}

  ngOnInit(): void {
    this.loading.showLoading();
    if (this.isStudent) {
      this.getUserData();
    }
    this.onInitForm();
    this.onSetEventsForm();

    if (this.studentClassesCourseId) {
      this.checkStudentFeedbackExist(parseInt(this.studentClassesCourseId));
    }

    this.loading.hideLoading();
  }

  private getUserData() {
    this.initStudent
      .getStudentUser()
      .pipe(first())
      .subscribe(
        (response: any) => {
          this.user = this.initStudent.user;
          this.student = response;
          this.f.studentId.setValue(this.student.id, { emitEvent: false });
          this.loading.hideLoading();
        },
        (error: any) => {
          this.errorMessage = 'Erro ao carregar o calendário.';
          this.loading.hideLoading();
        }
      );
  }

  private checkStudentFeedbackExist(studentClassesCourseId: number) {
    this.studentFeedbackService
      .getByStudentClassesCourseId(studentClassesCourseId)
      .pipe(first())
      .subscribe(
        (response: any) => {
          if (response.data) {
            this.f.studentId.setValue(response.data.studentId, {
              emitEvent: false,
            });
            this.f.studentClassesCourseId.setValue(
              response.data.studentClassesCourseId,
              { emitEvent: false }
            );
            this.f.feedbackText.setValue(response.data.feedbackText, {
              emitEvent: false,
            });
            this.f.rating.setValue(response.data.rating, { emitEvent: false });
            this.preview = true;
          } else {
            this.preview = false;
          }
          this.loading.hideLoading();
        },
        (error: any) => {
          this.errorMessage = 'Erro ao carregar o calendário.';
          this.preview = true;
          this.loading.hideLoading();
        }
      );
  }

  // Getter de conveniência para acessar facilmente os controles do formulário no HTML
  get f() {
    return this.feedbackForm.controls;
  }

  onInitForm() {
    this.feedbackForm = this.formBuilder.group({
      studentId: [0, Validators.required], // Preenche com o ID do aluno logado
      studentClassesCourseId: [
        this.studentClassesCourseId,
        Validators.required,
      ],
      feedbackText: [
        '',
        [Validators.required, Validators.maxLength(this.maxLengthFeedback)],
      ],
      rating: ['', [Validators.required, CustomValidators.ratingRange]],
    });
  }

  onSetEventsForm() {
    // NOVA LÓGICA PARA O CAMPO RATING
    this.f.rating.valueChanges.subscribe((value: string | null) => {
      if (value === null || value === undefined || value === '') {
        // Se o valor estiver vazio, não fazemos nada aqui,
        // Validators.required vai cuidar da validação de campo vazio.
        return;
      }

      let cleanedValue = String(value).replace(/\D/g, ''); // Remove tudo que não for dígito

      if (cleanedValue === '') {
        // Se o usuário apagou tudo para ficar vazio, permita.
        // Mas garantimos que o valor do FormControl seja realmente vazio se for diferente
        if (this.f.rating.value !== '') {
          this.f.rating.setValue('', { emitEvent: false });
        }
        return;
      }

      let numValue = parseInt(cleanedValue, 10);

      if (isNaN(numValue)) {
        // Caso algo bizarro aconteça e não seja um número após a limpeza, resetar.
        this.f.rating.setValue('', { emitEvent: false });
        return;
      }

      // Limitar a 1-10
      if (numValue < 1) {
        numValue = 1;
      } else if (numValue > 10) {
        numValue = 10;
      }

      const stringValue = numValue.toString();
      // Só atualiza se o valor realmente mudou para evitar chamadas desnecessárias
      if (this.f.rating.value !== stringValue) {
        // Atualiza o valor do form control sem disparar outro evento valueChanges
        // para evitar loops infinitos.
        this.f.rating.setValue(stringValue, { emitEvent: false });
      }
    });
  }

  dismissModal() {
    this.modalService.dismissAll();
    // Se precisar informar a tela pai que o modal foi fechado, pode usar um EventEmitter aqui
  }

  onSubmit(): void {
    this.loading.showLoading();
    this.submitted = true;
    this.validEntity = true; // Reseta o estado de validade
    this.errorMessage = ''; // Limpa mensagens de erro anteriores

    // Verifica se o formulário é inválido
    if (this.feedbackForm.invalid) {
      this.loading.hideLoading();
      this.submitted = false;
      this.validEntity = false;
      this.errorMessage =
        'Por favor, preencha todos os campos obrigatórios corretamente.';
      return;
    }

    const feedbackData: StudentFeedback = this.feedbackForm.value;

    this.studentFeedbackService
      .save(feedbackData.id, feedbackData, false)
      .pipe(first())
      .subscribe(
        (dataReturn: any) => {
          if (dataReturn.typeResponse === TypeResponse.Success) {
            this.dismissModal(); // Fecha o modal
            swal({
              type: 'success',
              title: 'Feedback enviado com sucesso!',
              text: 'Agradecemos sua avaliação sobre a aula.',
              showConfirmButton: false,
              timer: 1500,
            }).then(() => {
              // Opcional: Recarregar a página ou navegar após o sucesso
              // Ex: this.router.navigate(['/student/my-classes']);
            });
          } else {
            this.validEntity = false;
            this.errorMessage =
              dataReturn.message || 'Ocorreu um erro ao enviar o feedback.';
          }
          this.submitted = false;
          this.loading.hideLoading();
        },
        (error: any) => {
          this.validEntity = false;
          this.submitted = false;
          this.loading.hideLoading();
          this.errorMessage =
            error.error?.message ||
            'Erro de conexão ao enviar o feedback. Tente novamente.';
          console.error('Erro ao enviar feedback:', error);
        }
      );
  }
}
